import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PermissionChecklist, { useStaffMeta } from "@/components/app/PermissionChecklist";
import { toast } from "sonner";
import { ArrowLeft, Loader2, KeyRound } from "lucide-react";

const empty = { first_name: "", last_name: "", email: "", staff_role: "dipendente", permissions: [], active: true, password: "", password_confirm: "" };
const pwOk = (p) => p.length >= 8 && /[A-Za-z]/.test(p) && /\d/.test(p);

function PasswordReset({ id }) {
  const [pw, setPw] = useState({ password: "", password_confirm: "" });
  const save = async () => {
    if (!pwOk(pw.password)) return toast.error("Almeno 8 caratteri, con almeno una lettera e un numero");
    if (pw.password !== pw.password_confirm) return toast.error("Le password non coincidono");
    try { await api.post(`/staff/${id}/reset-password`, pw); toast.success("Password reimpostata · sessioni chiuse"); setPw({ password: "", password_confirm: "" }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <Card className="border-slate-200"><CardContent className="p-6 space-y-3">
      <div className="font-display font-bold text-lg flex items-center gap-2"><KeyRound className="w-5 h-5 text-amber-600" /> Reset password</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div><Label className="text-xs">Nuova password</Label><Input type="password" value={pw.password} onChange={(e) => setPw({ ...pw, password: e.target.value })} data-testid="staff-reset-password" /></div>
        <div><Label className="text-xs">Conferma password</Label><Input type="password" value={pw.password_confirm} onChange={(e) => setPw({ ...pw, password_confirm: e.target.value })} data-testid="staff-reset-password-confirm" /></div>
      </div>
      <Button variant="outline" onClick={save} data-testid="staff-reset-save">Reimposta password</Button>
    </CardContent></Card>
  );
}

export default function EmployeeForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const meta = useStaffMeta();
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const isNew = !id;
  const iAmEmployee = user?.staff_role === "dipendente";

  useEffect(() => {
    if (id) api.get(`/staff/${id}`).then((r) => setForm({ ...empty, ...r.data }));
    else setForm(empty);
  }, [id]);
  useEffect(() => { if (isNew && meta) setForm((f) => ({ ...f, permissions: meta.employee_defaults.filter((p) => !iAmEmployee || meta.mine.includes(p)) })); }, [isNew, meta, iAmEmployee]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) return toast.error("Nome, cognome ed e-mail sono obbligatori");
    if (isNew || form.password) {
      if (!pwOk(form.password)) return toast.error("La password deve avere almeno 8 caratteri, con almeno una lettera e un numero");
      if (form.password !== form.password_confirm) return toast.error("Le password non coincidono");
    }
    setBusy(true);
    try {
      const body = { ...form, password: form.password || null, password_confirm: form.password_confirm || null };
      if (isNew) await api.post("/staff", body); else await api.put(`/staff/${id}`, body);
      toast.success(isNew ? "Dipendente creato" : "Dipendente aggiornato");
      if (id === user?.id) await refresh();
      navigate("/admin/dipendenti");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!meta) return <div className="text-slate-500">Caricamento…</div>;
  const self = id === user?.id;
  return (
    <div className="space-y-6 max-w-4xl" data-testid="employee-form">
      <div>
        <button onClick={() => navigate("/admin/dipendenti")} className="text-sm text-slate-500 hover:text-amber-600 inline-flex items-center gap-1 mb-2" data-testid="staff-back"><ArrowLeft className="w-4 h-4" /> Elenco Dipendenti</button>
        <h1 className="font-display text-3xl font-extrabold text-slate-900">{isNew ? "Nuovo Dipendente" : `Modifica · ${form.first_name} ${form.last_name}`}</h1>
      </div>
      <Card className="border-slate-200"><CardContent className="p-6 space-y-4">
        <div className="font-display font-bold text-lg">Dati anagrafici</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Nome *</Label><Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="staff-first-name" /></div>
          <div><Label className="text-xs">Cognome *</Label><Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="staff-last-name" /></div>
          <div className="sm:col-span-2"><Label className="text-xs">E-mail * (univoca)</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="staff-email" /></div>
          {isNew && <>
            <div><Label className="text-xs">Password * (min 8, lettere e numeri)</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} data-testid="staff-password" /></div>
            <div><Label className="text-xs">Conferma Password *</Label><Input type="password" value={form.password_confirm} onChange={(e) => set("password_confirm", e.target.value)} data-testid="staff-password-confirm" /></div>
          </>}
          <div><Label className="text-xs">Ruolo</Label>
            <Select value={form.staff_role} onValueChange={(v) => set("staff_role", v)} disabled={self || iAmEmployee}>
              <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="dipendente">Dipendente</SelectItem><SelectItem value="amministratore">Amministratore</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="flex items-end gap-3 pb-2"><Switch checked={!!form.active} disabled={self} onCheckedChange={(v) => set("active", v)} data-testid="staff-active" /><Label>Account attivo</Label></div>
        </div>
      </CardContent></Card>
      <Card className="border-slate-200"><CardContent className="p-6 space-y-3">
        <div className="font-display font-bold text-lg">Gestione permessi</div>
        {form.staff_role === "amministratore"
          ? <div className="text-sm text-slate-600 bg-slate-50 border rounded-lg p-3">L'Amministratore ha accesso completo a tutte le funzionalità.</div>
          : <PermissionChecklist meta={meta} value={form.permissions} onChange={(v) => set("permissions", v)} lockedTo={iAmEmployee ? meta.mine : null} testPrefix="staff-perm" />}
      </CardContent></Card>
      {!isNew && <PasswordReset id={id} />}
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => navigate("/admin/dipendenti")}>Annulla</Button>
        <Button onClick={save} disabled={busy} className="bg-[#0F2942] hover:bg-[#1B3B5F]" data-testid="staff-save">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salva</Button>
      </div>
    </div>
  );
}
